import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { getBlogs } from "../api/blogAPI";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { FaSearch, FaClock, FaChevronRight } from "react-icons/fa";
import { GridSkeleton, BlogCardSkeleton } from "../components/LoadingSkeleton";

const Blogs = () => {
  const [blogs, setBlogs] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredBlogs, setFilteredBlogs] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchBlogs = async () => {
      try {
        const blogsData = await getBlogs();
        setBlogs(Array.isArray(blogsData) ? blogsData : []);
        setFilteredBlogs(Array.isArray(blogsData) ? blogsData : []);
        setError(null);
      } catch (error) {
        if (process.env.NODE_ENV === "development") {
          console.error("Error fetching blogs:", error);
        }
        setError("Failed to load blogs.");
      } finally {
        setLoading(false);
      }
    };

    fetchBlogs();
  }, []);

  // Modify search logic to include both title and content
  useEffect(() => {
    const filtered = blogs.filter(
      (blog) =>
        blog.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        blog.content.toLowerCase().includes(searchTerm.toLowerCase()) // Searching both title and content
    );
    setFilteredBlogs(filtered);
  }, [searchTerm, blogs]);

  const containerVariants = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    show: { opacity: 1, y: 0 },
  };

  return (
    <>
      <Helmet>
        <title>
          Blogs - David Seibold's Insights on Development and Technology
        </title>
        <meta
          name="description"
          content="Read David Seibold's latest blogs and insights on software development, M&A, and the tech industry. Stay updated with valuable content."
        />
        <meta
          property="og:title"
          content="Blogs - David Seibold's Insights on Development and Technology"
        />
        <meta
          property="og:description"
          content="Explore blogs covering software development, M&A, and the tech industry, written by David Seibold."
        />
        <meta property="og:url" content="https://daveyrockets.me/blogs" />
      </Helmet>

      <div className="container mx-auto px-4 md:px-6 lg:px-8 pb-12 md:pb-16 max-w-6xl text-gray-800 dark:text-brand-cream">
        <h1 className="heading-hero text-center text-gradient mb-6 md:mb-8 py-6 md:py-10">
          Words, Quotes, and Thoughts...
        </h1>

        <div className="relative mb-8 max-w-2xl mx-auto">
          <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search blogs..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-3 text-gray-800 dark:text-brand-cream bg-white dark:bg-gray-800 rounded-lg border border-gray-300 dark:border-gray-700 focus:outline-none focus:border-brand-gold focus:ring-2 focus:ring-brand-gold/20 transition-colors"
          />
        </div>

        {loading ? (
          <GridSkeleton
            count={6}
            component={BlogCardSkeleton}
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6"
          />
        ) : error ? (
          <div className="text-red-500 text-center p-4 bg-red-100 rounded-lg">
            {error}
          </div>
        ) : (
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="show"
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6"
          >
            {filteredBlogs.map((blog) => (
              <motion.div
                key={blog._id}
                variants={itemVariants}
                className="card-standard overflow-hidden shadow-lg hover:shadow-xl flex flex-col"
              >
                <div className="flex-1">
                  <h2 className="text-xl md:text-2xl font-semibold text-brand-gold mb-3 line-clamp-2">
                    {blog.title}
                  </h2>
                  <div className="flex items-center text-sm text-gray-400 mb-4">
                    <FaClock className="mr-2" />
                    {`${Math.ceil(
                      blog.content.split(" ").length / 200
                    )} min read`}
                  </div>
                  <p className="text-body text-base line-clamp-3">
                    {blog.content}
                  </p>
                </div>

                <Link
                  to={`/blogs/${blog._id}`}
                  className="block mt-6 p-4 bg-brand-darker rounded-lg text-brand-gold hover:text-brand-gold/80 transition-colors group border-t border-brand-purple/20"
                >
                  <span className="flex items-center justify-between">
                    Read More
                    <FaChevronRight className="transform group-hover:translate-x-1 transition-transform" />
                  </span>
                </Link>
              </motion.div>
            ))}
          </motion.div>
        )}
      </div>
    </>
  );
};

export default Blogs;
